import { useState, useEffect } from "react";
import { api } from "@/lib/apiClient";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Droplets, Lock, LogOut } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("folw_admin") || "");
  const [pw, setPw] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (t) => {
    try {
      const { data } = await api.get("/admin/data", { headers: { "x-admin-token": t } });
      setData(data);
    } catch {
      localStorage.removeItem("folw_admin");
      setToken("");
      toast.error("Session expired. Please sign in.");
    }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      localStorage.setItem("folw_admin", data.token);
      setToken(data.token);
    } catch { toast.error("Incorrect passphrase"); }
    setBusy(false);
  };

  const logout = () => { localStorage.removeItem("folw_admin"); setToken(""); setData(null); };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#05030A] grain flex items-center justify-center px-5">
        <form onSubmit={login} className="glass rounded-3xl p-10 w-full max-w-md text-center" data-testid="admin-login">
          <Droplets className="w-9 h-9 text-gold mx-auto" strokeWidth={1.3} />
          <h1 className="font-display text-3xl text-white mt-5">Ministry Portal</h1>
          <p className="text-sm text-[#B4ADC1] font-light mt-2">Fountain of Living Water · Care Team Only</p>
          <div className="relative mt-8">
            <Lock className="w-4 h-4 text-[#7A708A] absolute left-4 top-1/2 -translate-y-1/2" />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Passphrase" className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors" data-testid="admin-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full py-3.5 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors mt-4 disabled:opacity-50" data-testid="admin-submit">
            Enter Portal
          </button>
        </form>
      </div>
    );
  }

  const counts = data?.counts || {};

  return (
    <div className="min-h-screen bg-[#05030A] grain text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-4xl text-white flex items-center gap-3"><Droplets className="w-7 h-7 text-gold" strokeWidth={1.3} /> Ministry Portal</h1>
            <p className="text-sm text-[#B4ADC1] font-light mt-1">Live submissions from the Living Water ecosystem</p>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-sm text-[#B4ADC1] hover:text-gold transition-colors" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
          {[["Prayers", counts.prayers], ["Bible Studies", counts.studies], ["Contacts", counts.contacts], ["Donations", counts.donations], ["Chat Msgs", counts.chats]].map(([l, v]) => (
            <div key={l} className="glass rounded-2xl p-6">
              <p className="font-display text-4xl text-gold">{v ?? 0}</p>
              <p className="text-xs font-mono uppercase tracking-widest text-[#7A708A] mt-1">{l}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="prayers" className="mt-10">
          <TabsList className="bg-white/5 rounded-full p-1 flex-wrap h-auto">
            {["prayers", "studies", "contacts", "donations", "rsvps", "messages", "events", "library", "live", "chats"].map((t) => (
              <TabsTrigger key={t} value={t} className="rounded-full px-5 py-2 data-[state=active]:bg-gold data-[state=active]:text-black text-[#B4ADC1] capitalize">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="prayers"><Records rows={data?.prayers} render={(r) => (<><Cell k="Name" v={r.first_name} /><Cell k="Contact" v={r.contact} /><Cell k="Need" v={r.prayer_need} /><Cell k="Wants study" v={r.want_bible_study ? "Yes" : "No"} /><Cell k="Transport" v={r.want_transport ? "Yes" : "No"} /></>)} /></TabsContent>
          <TabsContent value="studies"><Records rows={data?.studies} render={(r) => (<><Cell k="Name" v={r.first_name} /><Cell k="Contact" v={r.contact} /><Cell k="Day" v={r.preferred_day || "—"} /><Cell k="Format" v={r.format} /></>)} /></TabsContent>
          <TabsContent value="contacts"><Records rows={data?.contacts} render={(r) => (<><Cell k="Name" v={r.name} /><Cell k="Contact" v={r.contact} /><Cell k="Message" v={r.message} /></>)} /></TabsContent>
          <TabsContent value="donations"><Records rows={data?.donations} render={(r) => (<><Cell k="Name" v={r.name} /><Cell k="Email" v={r.email} /><Cell k="Amount" v={`$${r.amount}`} /><Cell k="Fund" v={r.fund} /><Cell k="Frequency" v={r.frequency} /></>)} /></TabsContent>
          <TabsContent value="rsvps"><Records rows={data?.rsvps} render={(r) => (<><Cell k="Name" v={r.name} /><Cell k="Contact" v={r.contact} /><Cell k="Question" v={r.question || "—"} /></>)} /></TabsContent>
          <TabsContent value="messages"><MessagesManager token={token} messages={data?.messages} onChange={() => load(token)} /></TabsContent>
          <TabsContent value="events"><EventsManager token={token} events={data?.events} onChange={() => load(token)} /></TabsContent>
          <TabsContent value="library"><LibraryManager token={token} resources={data?.resources} onChange={() => load(token)} /></TabsContent>
          <TabsContent value="live"><LiveManager token={token} /></TabsContent>
          <TabsContent value="chats"><Records rows={data?.chats} render={(r) => (<><Cell k="Minister" v={r.minister_name} /><Cell k="Role" v={r.role} /><Cell k="Message" v={r.content} /></>)} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Records = ({ rows, render }) => {
  if (!rows) return <p className="text-[#7A708A] mt-8 font-mono text-sm">Loading…</p>;
  if (rows.length === 0) return <p className="text-[#7A708A] mt-8 font-mono text-sm">No records yet.</p>;
  return (
    <div className="space-y-3 mt-8" data-testid="admin-records">
      {rows.map((r, i) => (
        <div key={i} className="glass rounded-2xl p-5">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">{render(r)}</div>
          <p className="text-[0.65rem] font-mono text-[#5c5468] mt-3">{new Date(r.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
};

const Cell = ({ k, v }) => (
  <div className="flex gap-3 text-sm">
    <span className="text-[#7A708A] font-mono text-xs uppercase tracking-wider min-w-[90px] shrink-0">{k}</span>
    <span className="text-[#E7E3EE] font-light">{v}</span>
  </div>
);

const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#7A708A] outline-none focus:border-gold/50 transition-colors";

function MessagesManager({ token, messages, onChange }) {
  const [f, setF] = useState({ title: "", youtube_url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const headers = { "x-admin-token": token };

  const add = async (e) => {
    e.preventDefault();
    if (!f.title || !f.youtube_url) { toast.error("Title and YouTube link are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/messages", f, { headers });
      toast.success("Message published to the site.");
      setF({ title: "", youtube_url: "", note: "" });
      onChange();
    } catch { toast.error("Could not save message."); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`, { headers });
      toast.success("Message removed.");
      onChange();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="mt-8 space-y-6">
      <form onSubmit={add} className="glass rounded-2xl p-6 space-y-3" data-testid="admin-message-form">
        <p className="font-display text-xl text-white">Publish a Sunday Message (up to 4 shown — one per Sunday)</p>
        <p className="text-xs text-[#7A708A] font-mono">The 4 newest messages fill the month's slots, newest first. Older ones rotate out automatically.</p>
        <input className={inputCls} placeholder="Title (e.g. Sunday Message — Come to the Water)" value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="admin-message-title" />
        <input className={inputCls} placeholder="YouTube link (e.g. https://youtu.be/...)" value={f.youtube_url} onChange={(e) => setF((s) => ({ ...s, youtube_url: e.target.value }))} data-testid="admin-message-url" />
        <input className={inputCls} placeholder="Short note (optional)" value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="admin-message-note" />
        <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors disabled:opacity-50" data-testid="admin-message-submit">Publish Message</button>
      </form>

      <div className="space-y-3" data-testid="admin-message-list">
        {(messages || []).map((m) => (
          <div key={m.id} className="glass rounded-2xl p-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-white text-sm">{m.title}</p>
              <a href={m.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline break-all">{m.youtube_url}</a>
              <p className="text-[0.65rem] font-mono text-[#5c5468] mt-1">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <button onClick={() => remove(m.id)} className="text-xs text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-message-delete-${m.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsManager({ token, events, onChange }) {
  const [f, setF] = useState({ title: "", note: "", media_type: "video", youtube_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "x-admin-token": token };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    if (!f.title) { toast.error("Please add an event title."); return; }
    setBusy(true);
    try {
      if (f.media_type === "video") {
        if (!f.youtube_url) { toast.error("Please paste the YouTube link."); setBusy(false); return; }
        await api.post("/admin/events", f, { headers });
      } else {
        if (!file) { toast.error("Please choose a file to upload."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("title", f.title);
        fd.append("note", f.note);
        fd.append("media_type", f.media_type);
        fd.append("file", file);
        await api.post("/admin/events/upload", fd, { headers });
      }
      toast.success("Event published to the site.");
      setF({ title: "", note: "", media_type: "video", youtube_url: "" });
      setFile(null);
      onChange();
    } catch { toast.error("Could not save event."); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`, { headers });
      toast.success("Event removed.");
      onChange();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="mt-8 space-y-6">
      <form onSubmit={add} className="glass rounded-2xl p-6 space-y-3" data-testid="admin-event-form">
        <p className="font-display text-xl text-white">Publish an Upcoming Event (up to 4 shown, newest first)</p>
        <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
          {["video", "flyer", "audio"].map((t) => (
            <button type="button" key={t} onClick={() => set("media_type", t)} className={`flex-1 rounded-lg py-2.5 text-sm capitalize transition-colors ${f.media_type === t ? "bg-gold text-black" : "text-[#B4ADC1]"}`} data-testid={`admin-event-type-${t}`}>
              {t === "video" ? "Video link" : t === "flyer" ? "Flyer (image)" : "Audio file"}
            </button>
          ))}
        </div>
        <input className={inputCls} placeholder="Event title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-event-title" />
        <input className={inputCls} placeholder="Short note (optional)" value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="admin-event-note" />
        {f.media_type === "video" ? (
          <input className={inputCls} placeholder="YouTube link (video or short)" value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} data-testid="admin-event-url" />
        ) : (
          <input
            type="file"
            accept={f.media_type === "flyer" ? "image/*" : "audio/*"}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-[#B4ADC1] file:mr-4 file:px-5 file:py-2.5 file:rounded-full file:border-0 file:bg-gold file:text-black file:font-medium file:cursor-pointer bg-white/5 border border-white/10 rounded-xl p-2"
            data-testid="admin-event-file"
          />
        )}
        <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors disabled:opacity-50" data-testid="admin-event-submit">
          {busy ? "Publishing…" : "Publish Event"}
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-event-list">
        {(events || []).map((ev) => (
          <div key={ev.id} className="glass rounded-2xl p-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-white text-sm">{ev.title} <span className="text-[0.6rem] font-mono uppercase tracking-wider text-gold ml-2">{ev.media_type}</span></p>
              {ev.youtube_url && <a href={ev.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline break-all">{ev.youtube_url}</a>}
              {ev.original_filename && <p className="text-xs text-[#B4ADC1]">{ev.original_filename}</p>}
              <p className="text-[0.65rem] font-mono text-[#5c5468] mt-1">{new Date(ev.created_at).toLocaleString()}</p>
            </div>
            <button onClick={() => remove(ev.id)} className="text-xs text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-event-delete-${ev.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LibraryManager({ token, resources, onChange }) {
  const [f, setF] = useState({ kind: "class", title: "", note: "", source: "youtube", youtube_url: "", link_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "x-admin-token": token };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    if (!f.title) { toast.error("Please add a title."); return; }
    setBusy(true);
    try {
      if (f.source === "upload") {
        if (!file) { toast.error("Please choose a file."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("kind", f.kind);
        fd.append("title", f.title);
        fd.append("note", f.note);
        fd.append("file", file);
        await api.post("/admin/resources/upload", fd, { headers });
      } else {
        const payload = { kind: f.kind, title: f.title, note: f.note };
        if (f.source === "youtube") {
          if (!f.youtube_url) { toast.error("Paste the YouTube link."); setBusy(false); return; }
          payload.youtube_url = f.youtube_url;
        } else {
          if (!f.link_url) { toast.error("Paste the link."); setBusy(false); return; }
          payload.link_url = f.link_url;
        }
        await api.post("/admin/resources", payload, { headers });
      }
      toast.success("Published to The Library.");
      setF({ kind: f.kind, title: "", note: "", source: f.source, youtube_url: "", link_url: "" });
      setFile(null);
      onChange();
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/resources/${id}`, { headers });
      toast.success("Removed.");
      onChange();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="mt-8 space-y-6">
      <form onSubmit={add} className="glass rounded-2xl p-6 space-y-3" data-testid="admin-library-form">
        <p className="font-display text-xl text-white">Add to The Library — Classes, Books & Videos</p>
        <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
          {["class", "book", "video"].map((k) => (
            <button type="button" key={k} onClick={() => set("kind", k)} className={`flex-1 rounded-lg py-2.5 text-sm capitalize transition-colors ${f.kind === k ? "bg-gold text-black" : "text-[#B4ADC1]"}`} data-testid={`admin-library-kind-${k}`}>{k}</button>
          ))}
        </div>
        <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
          {[["youtube", "YouTube link"], ["upload", "Upload file (PDF, image, audio, video)"], ["link", "External link"]].map(([s, l]) => (
            <button type="button" key={s} onClick={() => set("source", s)} className={`flex-1 rounded-lg py-2.5 text-xs sm:text-sm transition-colors ${f.source === s ? "bg-gold text-black" : "text-[#B4ADC1]"}`} data-testid={`admin-library-source-${s}`}>{l}</button>
          ))}
        </div>
        <input className={inputCls} placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-library-title" />
        <input className={inputCls} placeholder="Short note (optional)" value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="admin-library-note" />
        {f.source === "youtube" && <input className={inputCls} placeholder="YouTube link" value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} data-testid="admin-library-url" />}
        {f.source === "link" && <input className={inputCls} placeholder="External link (https://...)" value={f.link_url} onChange={(e) => set("link_url", e.target.value)} data-testid="admin-library-link" />}
        {f.source === "upload" && (
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-[#B4ADC1] file:mr-4 file:px-5 file:py-2.5 file:rounded-full file:border-0 file:bg-gold file:text-black file:font-medium file:cursor-pointer bg-white/5 border border-white/10 rounded-xl p-2"
            data-testid="admin-library-file"
          />
        )}
        <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors disabled:opacity-50" data-testid="admin-library-submit">
          {busy ? "Publishing…" : "Publish to Library"}
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-library-list">
        {(resources || []).map((r) => (
          <div key={r.id} className="glass rounded-2xl p-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-white text-sm">{r.title} <span className="text-[0.6rem] font-mono uppercase tracking-wider text-gold ml-2">{r.kind}</span></p>
              {r.youtube_url && <a href={r.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline break-all">{r.youtube_url}</a>}
              {r.link_url && <a href={r.link_url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline break-all">{r.link_url}</a>}
              {r.original_filename && <p className="text-xs text-[#B4ADC1]">{r.original_filename}</p>}
              <p className="text-[0.65rem] font-mono text-[#5c5468] mt-1">{new Date(r.created_at).toLocaleString()}</p>
            </div>
            <button onClick={() => remove(r.id)} className="text-xs text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-library-delete-${r.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveManager({ token }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/live-session").then(({ data }) => setF(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/live-session", f, { headers: { "x-admin-token": token } });
      toast.success("Live session details updated.");
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };

  if (!f) return <p className="text-[#7A708A] mt-8 font-mono text-sm">Loading…</p>;

  return (
    <form onSubmit={save} className="glass rounded-2xl p-6 space-y-3 mt-8" data-testid="admin-live-form">
      <p className="font-display text-xl text-white">"Ask an Apostolic Minister" — Live Session Settings</p>
      <input className={inputCls} placeholder="Session title" value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="admin-live-title" />
      <input className={inputCls} placeholder="Schedule (e.g. Wednesdays · 7:00 PM Central)" value={f.schedule} onChange={(e) => setF((s) => ({ ...s, schedule: e.target.value }))} data-testid="admin-live-schedule" />
      <input className={inputCls} placeholder="Join link (Zoom / YouTube Live — leave blank until ready)" value={f.join_url || ""} onChange={(e) => setF((s) => ({ ...s, join_url: e.target.value }))} data-testid="admin-live-url" />
      <textarea className={`${inputCls} min-h-24`} placeholder="Description shown to visitors" value={f.details || ""} onChange={(e) => setF((s) => ({ ...s, details: e.target.value }))} data-testid="admin-live-details" />
      <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors disabled:opacity-50" data-testid="admin-live-save">Save Live Session</button>
    </form>
  );
}
